// src/lib/menage-mois.js
//
// LES RÈGLES DE LA PAGE « MOIS » — helpers PURS. Aucun accès réseau, aucun DOM,
// aucune écriture. Le rendu vit dans src/views/MenageCalendrierView.jsx ; les
// RÈGLES vivent ici, parce qu'une règle qui n'existe que dans du JSX ne peut
// être ni mesurée ni cassée exprès.
//
// ⚠️ LE DÉFAUT CENTRAL QUE CE MODULE CORRIGE. Une case affichait un NOMBRE NU
// dont le sens changeait avec la couleur : corail = le total du jour, rouge = le
// nombre de non-validés. Deux cases marquées « 2 » ne parlaient donc pas de la
// même chose, et rien à l'écran ne le disait. Chaque état porte désormais SON
// LIBELLÉ, écrit en toutes lettres, et le nombre ne voyage plus seul.
//
// ⚠️ ET PLUS RIEN EN INFOBULLE. Cette page s'utilise au doigt : ce qui
// n'apparaît qu'au survol n'existe pas sur mobile. Tout ce que portait un
// `title=` monte dans la case ou disparaît.
//
// AUCUN MONTANT, aucun nom de voyageur n'entre ici ni n'en sort.

import { dureeDuMenage, heuresDeTravail, formaterDuree } from './menage-frise'
import { decalerJour } from './menage-suivi-pilotage'

const SKIPPED = 'skipped'
const COMPLETED = 'completed'

/**
 * ⚠️ PRIORITÉ DES ÉTATS, ET ELLE N'EST PAS ARBITRAIRE. Un jour à venir dont des
 * ménages n'ont pas d'agent porte « sans agent », pas « prévus » : c'est ce sur
 * quoi on peut agir MAINTENANT. Afficher l'état le plus rassurant des deux
 * reviendrait à cacher le seul qui demande quelque chose.
 */
/**
 * L'ÉTAT D'UN JOUR, et le libellé qui va avec.
 *
 * ⚠️ LE JOUR MÊME N'EST JAMAIS EN RETARD. Un agent a jusqu'au bout de sa
 * journée pour valider ; crier « en retard » à 9 h du matin serait faux autant
 * qu'anxiogène. Règle conservée de la version précédente — elle était bonne.
 *
 * @returns {{cle:string, nb:number, libelle:string}}
 */
export function etatDuJour({ dateIso, todayIso, total = 0, aValider = 0, sansAgent = 0 }) {
  // « JE DOIS SAVOIR QUEL JOUR ON EST » (Dimitri 2026-08-19) : un aujourd'hui
  // sans ménage garde sa bordure — le jour courant se distingue TOUJOURS.
  if (!total) {
    return dateIso === todayIso
      ? { cle: 'aujourdhui', nb: 0, libelle: 'rien de prévu' }
      : { cle: 'vide', nb: 0, libelle: '' }
  }

  const passe = typeof dateIso === 'string' && typeof todayIso === 'string' && dateIso < todayIso
  const aujourdhui = dateIso === todayIso

  if (passe && aValider > 0) {
    // « non validé » — le MOT de la frise (motif après 48h). « à valider »
    // disait la même chose autrement ; deux mots pour un fait, c'est un de trop.
    return { cle: 'retard', nb: aValider, libelle: `${aValider} non validé${aValider > 1 ? 's' : ''}` }
  }
  if (aujourdhui) {
    return aValider > 0
      ? { cle: 'aujourdhui', nb: aValider, libelle: `${aValider} en cours` }
      : { cle: 'aujourdhui', nb: total, libelle: `${total} validés` }
  }
  if (!passe && sansAgent > 0) {
    return { cle: 'sans_agent', nb: sansAgent, libelle: `${sansAgent} sans agent` }
  }
  if (aValider === 0) {
    return { cle: 'valide', nb: total, libelle: `${total} validé${total > 1 ? 's' : ''}` }
  }
  return { cle: 'a_venir', nb: total, libelle: `${total} prévu${total > 1 ? 's' : ''}` }
}

/**
 * CE QUE LA PASTILLE PORTE — un nombre, parfois une icône, jamais une date.
 *
 * ⚠️ C'EST LA FORME QUI SÉPARE, PAS LA COULEUR. Le numéro du jour et le compte
 * avaient la même taille et le même gris : on ne savait pas lequel était une
 * date. Un chiffre dans un rond n'est jamais une date — et ça reste vrai pour
 * quelqu'un qui distingue mal les couleurs, ce qu'aucune palette ne garantit.
 *
 * Le LIBELLÉ complet (« 2 à valider ») ne disparaît pas pour autant : il reste
 * le `aria-label` de la case. Ce que l'œil lit à la forme, une synthèse vocale
 * doit pouvoir le lire en toutes lettres.
 *
 * @returns {{nombre:string, icone:'check'|'sans-agent'|null, aria:string}}
 */
export function pastilleDuJour(etat) {
  // nb 0 (aujourd'hui sans ménage) : pas de pastille « 0 » — la bordure suffit.
  if (!etat || etat.cle === 'vide' || !etat.nb) return null
  const nombre = String(etat.nb)
  if (etat.cle === 'valide') return { nombre, icone: 'check', aria: etat.libelle }
  if (etat.cle === 'sans_agent') return { nombre, icone: 'sans-agent', aria: etat.libelle }
  return { nombre, icone: null, aria: etat.libelle }
}

/**
 * AGRÉGATION DU MOIS — un index par jour, plus les totaux.
 *
 * ⚠️ LES INTERVENTIONS COMPTENT. La grille ne comptait que des ménages : une
 * intervention posée dans la frise (« attendre le plombier ») n'existait pas
 * dans le mois, alors qu'elle occupe l'agent autant qu'un ménage et qu'elle
 * peut très bien être la seule chose prévue un jour donné. Un mois qui ignore
 * une moitié du travail n'est pas un mois.
 *
 * Un `skipped` reste exclu : il a été annulé, il n'est plus du travail.
 */
export function agregerMois({ runs = [], interventions = [], todayIso } = {}) {
  const parJour = {}
  const poser = (iso) => {
    if (!parJour[iso]) {
      parJour[iso] = { total: 0, valides: 0, aValider: 0, sansAgent: 0, interventions: 0 }
    }
    return parJour[iso]
  }

  for (const r of Array.isArray(runs) ? runs : []) {
    const iso = r?.scheduled_date
    const statut = r?.status || 'scheduled'
    if (!iso || typeof iso !== 'string' || statut === SKIPPED) continue
    const j = poser(iso)
    j.total += 1
    if (statut === COMPLETED) j.valides += 1
    else j.aValider += 1
    if (!r.assigned_provider_email) j.sansAgent += 1
  }

  for (const it of Array.isArray(interventions) ? interventions : []) {
    const iso = it?.scheduled_date
    if (!iso || typeof iso !== 'string' || it.statut === SKIPPED) continue
    const j = poser(iso)
    j.total += 1
    j.interventions += 1
    if (it.statut === COMPLETED) j.valides += 1
    else j.aValider += 1
    if (!it.assigned_provider_email) j.sansAgent += 1
  }

  const jours = Object.keys(parJour).sort()
  let total = 0
  let valides = 0
  let sansAgent = 0
  const joursEnRetard = []
  const joursSansAgent = []
  for (const iso of jours) {
    const j = parJour[iso]
    total += j.total
    valides += j.valides
    sansAgent += j.sansAgent
    const etat = etatDuJour({ dateIso: iso, todayIso, ...j })
    if (etat.cle === 'retard') joursEnRetard.push(iso)
    if (etat.cle === 'sans_agent') joursSansAgent.push(iso)
  }

  return {
    parJour,
    total,
    valides,
    aVenir: total - valides,
    sansAgent,
    joursEnRetard,
    joursSansAgent,
  }
}

/**
 * LA CHARGE DE CHAQUE AGENT SUR LE MOIS — le chiffre qui n'existait nulle part.
 *
 * ⚠️ POURQUOI `heuresDeTravail` ET NON `chargeAgent`. `chargeAgent` compare le
 * travail à la FENÊTRE dans laquelle il doit tenir (« 7 h sur 6 h ») : c'est une
 * mesure de JOURNÉE, née de l'imbrication des créneaux. Sur un mois, « sur 6 h »
 * ne veut plus rien dire — la fenêtre serait la somme de trente fenêtres
 * indépendantes, un nombre qui ne correspond à aucune contrainte réelle. On
 * garde donc le seul terme qui reste vrai à cette échelle : le temps de travail.
 *
 * `dureeDe(run)` doit rendre la durée en minutes — l'appelant y branche
 * `dureeDuMenage`, jamais un second calcul.
 */
export function chargeParAgent({ runs = [], interventions = [], dureeDe } = {}) {
  return chargePar({ runs, interventions, dureeDe, cle: 'email',
    cleDe: (x) => x?.assigned_provider_email || null })
}

/**
 * LA MÊME CHARGE, PAR LOGEMENT (Dimitri 2026-08-14 : « je dois pouvoir faire
 * mon compte de passages par agent ET par logement »). `logement_id` est
 * NOT NULL sur les deux tables — aucune ligne « sans logement » n'existe.
 */
export function chargeParLogement({ runs = [], interventions = [], dureeDe } = {}) {
  return chargePar({ runs, interventions, dureeDe, cle: 'logementId',
    cleDe: (x) => x?.logement_id || null })
}

// ⚠️ TERMINÉS SEULEMENT (Dimitri 2026-08-22) : le compte et les heures ne
// retiennent que les checklists VALIDÉES — un ménage prévu ou commencé
// n'est pas du travail fait, l'annoncer mentait. La charge à venir reste
// visible À PART : `aVenir` (prévus + commencés, jamais mélangés au fait).
// Les heures restent du temps PRÉVU (durée configurée) : l'app ne
// chronomètre pas le temps passé.
function chargePar({ runs = [], interventions = [], dureeDe, cle, cleDe } = {}) {
  const duree = typeof dureeDe === 'function' ? dureeDe : () => null
  const par = new Map()
  const entree = (valeurCle) => {
    if (!par.has(valeurCle)) par.set(valeurCle, { [cle]: valeurCle, nb: 0, aVenir: 0, blocs: [] })
    return par.get(valeurCle)
  }
  const ajouter = (valeurCle, minutes, fait) => {
    if (!valeurCle) return // « personne » n'est pas un agent : compté à part
    const e = entree(valeurCle)
    if (fait) { e.nb += 1; e.blocs.push({ dureeMin: minutes }) } else { e.aVenir += 1 }
  }

  for (const r of Array.isArray(runs) ? runs : []) {
    const statut = r?.status || 'scheduled'
    if (statut === SKIPPED) continue
    ajouter(cleDe(r), duree(r), statut === COMPLETED)
  }
  for (const it of Array.isArray(interventions) ? interventions : []) {
    if (it?.statut === SKIPPED) continue
    ajouter(cleDe(it), Number(it?.duree_min) > 0 ? Number(it.duree_min) : null,
      it?.statut === COMPLETED)
  }

  return [...par.values()]
    .map(({ blocs, ...reste }) => ({ ...reste, minutes: heuresDeTravail(blocs) }))
    .filter((e) => e.nb > 0 || e.aVenir > 0)
    .sort((a, b) => b.minutes - a.minutes || String(a[cle]).localeCompare(String(b[cle])))
}

/** « 34 passages · 51 h » — le libellé d'une ligne de charge.
 *  ⚠️ « passages », pas « ménages » : le compte mêle ménages ET interventions
 *  (correction Dimitri 2026-08-14 — « 31 ménages » mentait dès la première
 *  intervention). */
export function libelleCharge({ nb, minutes }) {
  const n = `${nb} passage${nb > 1 ? 's' : ''}`
  if (!minutes) return n
  return `${n} · ${formaterDuree(minutes)}`
}

// ── LA SEMAINE LA PLUS CHARGÉE ──────────────────────────────────────────────

/** Le LUNDI de la semaine d'une date ISO. Semaine ISO : lundi → dimanche. */
export function debutSemaine(iso) {
  const [y, m, d] = String(iso).split('-').map(Number)
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay() // 0 = dimanche
  const lundiDelta = dow === 0 ? -6 : 1 - dow
  return decalerJour(iso, lundiDelta)
}

/**
 * LA SEMAINE LA PLUS CHARGÉE du mois — celle où l'équipe encaisse le plus.
 *
 * ⚠️ ON COMPTE LES JOURS DU MOIS AFFICHÉ, pas la semaine entière. Une semaine à
 * cheval sur deux mois serait sinon comptée deux fois, et la « semaine la plus
 * chargée » d'août pourrait être portée par des ménages de juillet — un chiffre
 * juste sur une période que personne ne regarde.
 *
 * @returns {{debut:string, fin:string, nb:number, sansAgent:number}|null}
 */
/** TOUTES les semaines du mois, chronologiques — { debut, fin, nb, sansAgent }.
 *  C'est la matière de la carte « Ce qui arrive » (2026-08-15) : la charge par
 *  semaine ET ses trous, pour voir AVANT d'arriver si ça va tenir. */
export function semainesDuMois(parJour) {
  const jours = Object.keys(parJour || {}).sort()
  const par = new Map()
  for (const iso of jours) {
    const lundi = debutSemaine(iso)
    if (!lundi) continue
    if (!par.has(lundi)) par.set(lundi, { debut: lundi, fin: decalerJour(lundi, 6), nb: 0, sansAgent: 0 })
    const s = par.get(lundi)
    s.nb += parJour[iso].total
    s.sansAgent += parJour[iso].sansAgent
  }
  return [...par.values()].sort((a, b) => a.debut.localeCompare(b.debut))
}

export function semaineLaPlusChargee(parJour) {
  const list = semainesDuMois(parJour)
  if (!list.length) return null
  return [...list].sort((a, b) => b.nb - a.nb || a.debut.localeCompare(b.debut))[0]
}

// ── LIBELLÉS DE DATES ───────────────────────────────────────────────────────

/** « 13 août » — sans l'année, on est dans le mois affiché. */
export function jourEtMois(iso) {
  const d = new Date(`${iso}T12:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long' }).format(d)
}

/**
 * « 13 et 16 août » / « 13, 16 et 22 août » / « 13 août et 3 autres jours ».
 *
 * On ÉNUMÈRE jusqu'à trois dates puis on compte : au-delà, une liste de dates
 * cesse d'être lisible et devient un pavé qu'on saute.
 */
export function listerDates(isos, max = 3) {
  const l = Array.isArray(isos) ? isos.filter(Boolean) : []
  if (!l.length) return ''
  if (l.length <= max) {
    const jours = l.map((iso) => {
      const d = new Date(`${iso}T12:00:00`)
      return Number.isNaN(d.getTime()) ? iso : String(d.getDate())
    })
    const mois = jourEtMois(l[l.length - 1]).replace(/^\d+\s+/, '')
    if (jours.length === 1) return `${jours[0]} ${mois}`
    return `${jours.slice(0, -1).join(', ')} et ${jours[jours.length - 1]} ${mois}`
  }
  const reste = l.length - 1
  return `${jourEtMois(l[0])} et ${reste} autre${reste > 1 ? 's' : ''} jour${reste > 1 ? 's' : ''}`
}

/** « du mois d'août » / « du mois de septembre » — élision devant voyelle. */
function duMois(monthStr) {
  const [y, m] = String(monthStr || '').split('-').map(Number)
  if (!y || !m) return null
  const nom = new Date(y, m - 1, 1).toLocaleDateString('fr-FR', { month: 'long' })
  return /^[aeiouyàâéèêëîïôùû]/i.test(nom) ? `d'${nom}` : `de ${nom}`
}


/** Le sous-titre du bandeau : « 86 passages · 4 jours à rattraper ».
 *  « passages » : même règle que libelleCharge (le total mêle les deux). */
export function sousTitreMois({ total, joursEnRetard }) {
  const n = Array.isArray(joursEnRetard) ? joursEnRetard.length : 0
  return {
    total: `${total} passage${total > 1 ? 's' : ''}`,
    retard: n ? `${n} jour${n > 1 ? 's' : ''} à rattraper` : null,
  }
}

/**
 * LE TITRE DE LA PAGE.
 *
 * ⚠️ « Programme du mois · Orch » laissait croire à un programme PERSONNEL —
 * un reste de la version agent, où l'écran montre bien les ménages de la
 * personne connectée. Pour un orchestrateur, c'est le mois de son ÉQUIPE. Même
 * écran, même code : seuls le titre et le périmètre changent.
 */
export function titreMois(role) {
  return role === 'orchestrateur' ? "Le mois de l'équipe" : 'Mon mois'
}

/** La durée d'un ménage, branchée sur la règle unique (jamais recopiée). */
export function dureeDuRun(run, logement) {
  return dureeDuMenage(run, logement)
}
